import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TimesheetsService } from "../../services/timesheets";
import { UserAssignees } from "../../../SharedModule/types/shared-module-types";
import {
  NonBillableReason,
  PayPeriods,
  TaskType,
} from "../../types/times-module-types";
import { UsersService } from "../../../UsersModule/services/users";
import { addElementToArray } from "../../utils/timesUtils";

export const fetchMetaTimes = async (
  id: string,
  isTimeAdmin: boolean,
  isTimeViewer: boolean,
  isDeliveryTimeAdmin: boolean
) => {
  let userAssignees,
    payPeriods,
    taskTypesData,
    nonBillableReasonsData,
    users,
    timeConfirmationData,
    costCentersTimeConfirmation;
  if (isTimeAdmin || isTimeViewer || isDeliveryTimeAdmin) {
    [
      userAssignees,
      payPeriods,
      taskTypesData,
      nonBillableReasonsData,
      users,
      timeConfirmationData,
    ] = await Promise.all([
      UsersService.getUserAssignees(id),
      TimesheetsService.getPayPeriods(),
      TimesheetsService.getTaskTypes(),
      TimesheetsService.getNonBillableReasons(),
      UsersService.getUserList(),
      TimesheetsService.getTimeConfirmationFilters(),
    ]);

    costCentersTimeConfirmation = [];
    if (timeConfirmationData) {
      addElementToArray(
        costCentersTimeConfirmation,
        timeConfirmationData.costCenters
      );
    }
  } else {
    [userAssignees, payPeriods, taskTypesData, nonBillableReasonsData, users] =
      await Promise.all([
        UsersService.getUserAssignees(id),
        TimesheetsService.getPayPeriods(),
        TimesheetsService.getTaskTypes(),
        TimesheetsService.getNonBillableReasons(),
        UsersService.getUserList(),
      ]);
    costCentersTimeConfirmation = null;
  }
  return {
    userAssignees:
      userAssignees?.data.map((assignee: UserAssignees) => ({
        value: assignee.id,
        label: assignee.fullName,
        email: assignee.email,
        entryReminder: assignee.entryReminder,
      })) || [],
    payPeriods: payPeriods.data.data,
    taskTypes:
      taskTypesData?.data.map((task: TaskType) => ({
        value: task.taskTypeId,
        label: task.taskTypeName,
        category: task.category,
      })) || [],
    nonBillableReasons:
      nonBillableReasonsData?.data.map((elem: NonBillableReason) => ({
        value: elem.id,
        label: elem.name,
      })) || [],
    timeUsers:
      users.map((elem: UserAssignees) => ({
        value: elem.id,
        label: elem.fullName,
        isActive: elem.isActive,
        email: elem.email,
        entryReminder: elem.entryReminder,
      })) || null,
    costCentersTimeConfirmation: costCentersTimeConfirmation || null,
  };
};

export type MetaTimesState = {
  userAssignees: Array<any> | null;
  payPeriods: PayPeriods | null;
  taskTypes: Array<any> | null;
  nonBillableReasons: Array<any> | null;
  timeUsers: Array<any> | null;
  costCentersTimeConfirmation: Array<any> | null;
  timesDataLoaded: boolean;
};

type SetItem = Partial<MetaTimesState>;

const initialState: MetaTimesState = {
  userAssignees: null,
  payPeriods: null,
  taskTypes: null,
  nonBillableReasons: null,
  timeUsers: null,
  costCentersTimeConfirmation: null,
  timesDataLoaded: false,
};

export const metaSlice = createSlice({
  name: "metaTimes",
  initialState,
  reducers: {
    // Remember Redux Toolkit allows us to write "mutating" logic in reducers.
    setMetaTimes: (state: MetaTimesState, action: PayloadAction<SetItem>) => {
      return { ...state, ...action.payload, timesDataLoaded: true };
    },
    updateEntryReminder: (
      state: MetaTimesState,
      action: PayloadAction<any>
    ) => {
      if (state.userAssignees) {
        state.userAssignees = state.userAssignees.map((user) =>
          user.email === action.payload.email
            ? { ...user, entryReminder: action.payload.entryReminder }
            : user
        );
      }
      if (state.timeUsers) {
        state.timeUsers = state.timeUsers.map((user) =>
          user.email === action.payload.email
            ? { ...user, entryReminder: action.payload.entryReminder }
            : user
        );
      }
    },
  },
});

export const { setMetaTimes, updateEntryReminder } = metaSlice.actions;

// Selectors
export const selectUserAssignees = ({
  metaTimes,
}: {
  metaTimes: MetaTimesState;
}) => metaTimes.userAssignees;

export const selectPayPeriods = ({
  metaTimes,
}: {
  metaTimes: MetaTimesState;
}) => metaTimes.payPeriods;

export const selectTaskTypes = ({ metaTimes }: { metaTimes: MetaTimesState }) =>
  metaTimes.taskTypes;

export const selectNonBillableReasons = ({
  metaTimes,
}: {
  metaTimes: MetaTimesState;
}) => metaTimes.nonBillableReasons;

export const selectTimeUsers = ({ metaTimes }: { metaTimes: MetaTimesState }) =>
  metaTimes.timeUsers;

export const SelectCostCentersTimeConfirmation = ({
  metaTimes,
}: {
  metaTimes: MetaTimesState;
}) => metaTimes.costCentersTimeConfirmation;

export const selectTimesDataLoaded = ({
  metaTimes,
}: {
  metaTimes: MetaTimesState;
}) => metaTimes.timesDataLoaded;

export const selectMetaTimesData = (state: { metaTimes: MetaTimesState }) => {
  return {
    userAssignees: selectUserAssignees(state),
    payPeriods: selectPayPeriods(state),
    taskTypes: selectTaskTypes(state),
    nonBillableReasons: selectNonBillableReasons(state),
    timeUsers: selectTimeUsers(state),
    costCentersTimeConfirmation: SelectCostCentersTimeConfirmation(state),
    timesDataLoaded: selectTimesDataLoaded(state),
  };
};

export default metaSlice.reducer;
